import React, { useState, useEffect, useRef } from 'react';
import { jsPDF } from 'jspdf';
import type { AnalysisResults } from '../../types/frontend';

interface SelectedItems {
  rules: Set<string>;
  symbols: Set<string>;
  rituals: Set<string>;
}

interface StickyDreamSummaryProps {
  originalDream: AnalysisResults['originalDream'];
  sentimentAnalysis?: AnalysisResults['sentimentAnalysis'];
  selectedItems?: SelectedItems;
  results?: AnalysisResults;
}

export const StickyDreamSummary: React.FC<StickyDreamSummaryProps> = ({
  originalDream,
  sentimentAnalysis,
  selectedItems,
  results,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareMenuRef = useRef<HTMLDivElement>(null);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShowShareMenu(false);
      }
    };

    if (showShareMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showShareMenu]);

  // Truncate dream text for sticky card
  const MAX_LENGTH = 200;
  const dreamText = originalDream.text;
  const shouldTruncate = dreamText.length > MAX_LENGTH;
  const displayText =
    shouldTruncate && !isExpanded ? `${dreamText.slice(0, MAX_LENGTH)}...` : dreamText;

  // Calculate total selected
  const totalSelected = (selectedItems?.rules.size || 0) + 
                        (selectedItems?.symbols.size || 0) + 
                        (selectedItems?.rituals.size || 0);

  const handleShare = async () => {
    setShowShareMenu(false);
    let shareText = `Dream analyzed by Šāʾilu:\n\n"${dreamText}"\n\nSentiment: ${sentimentAnalysis?.summary.dominantSentiment || 'unknown'}`;
    
    // Add selected items if any
    if (totalSelected > 0 && results) {
      shareText += '\n\n--- MY INTERPRETATION ---';
      
      // Add selected rules
      if (selectedItems?.rules.size) {
        shareText += '\n\nApplicable Omina:';
        results.primaryRules
          .filter(r => selectedItems.rules.has(r.id))
          .forEach(rule => {
            shareText += `\n• If ${rule.condition}, then ${rule.outcome}`;
          });
      }
      
      // Add selected symbols
      if (selectedItems?.symbols.size) {
        shareText += '\n\nMeaningful Symbols:';
        results.symbolicMeanings
          .filter(e => selectedItems.symbols.has(e.id))
          .forEach(entity => {
            shareText += `\n• ${entity.name}: ${entity.summary}`;
          });
      }
      
      // Add selected rituals
      if (selectedItems?.rituals.size) {
        shareText += '\n\nRecommended Rituals:';
        results.ritualRecommendations
          ?.filter(r => selectedItems.rituals.has(r.ritual.id))
          .forEach(rec => {
            shareText += `\n• ${rec.ritual.title}`;
          });
      }
    }
    
    shareText += '\n\nAnalyze your dreams at sailu.gbe.games';
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Dream Analysis - Šāʾilu',
          text: shareText,
        });
      } catch (err) {
        // User cancelled or error occurred
        console.log('Share cancelled or failed:', err);
      }
    } else {
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(shareText);
      alert('Dream summary copied to clipboard!');
    }
  };

  const dateAnalyzed = new Date().toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  });

  const handleDownloadPDF = async () => {
    setShowShareMenu(false);
    
    if (!results || !selectedItems) {
      alert('No data to export');
      return;
    }

    // Helper functions for formatting
    const formatSource = (source: string) => {
      return source
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };

    const formatCitation = (citation: string) => {
      const match = citation.match(/^([a-z]+)(\d{4})$/i);
      if (match) {
        const name = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
        const year = match[2];
        return `${name} (${year})`;
      }
      return citation;
    };

    const formatCategory = (category: string) => {
      return category
        .replace(/_/g, ' and ')
        .split(' ')
        .map((word, i) => {
          const lowerWords = ['and', 'or', 'of', 'the', 'in', 'a', 'an'];
          if (i > 0 && lowerWords.includes(word.toLowerCase())) {
            return word.toLowerCase();
          }
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
    };

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - margin * 2;
      let y = margin;

      // Helper to add new page if needed
      const checkPageBreak = (needed: number) => {
        if (y + needed > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
      };

      // Header
      doc.setFillColor(245, 242, 235);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      doc.setFont('times', 'italic');
      doc.setFontSize(24);
      doc.text('Sailu', margin, y);
      y += 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(122, 117, 104);
      doc.text('Dream Analysis Report', margin, y);
      y += 5;
      doc.text(dateAnalyzed, margin, y);
      y += 15;

      // Dream
      doc.setFontSize(14);
      doc.setTextColor(29, 26, 21);
      doc.setFont('helvetica', 'bold');
      doc.text('Your Dream', margin, y);
      y += 8;
      
      if (sentimentAnalysis) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        const sentiment = sentimentAnalysis.summary.dominantSentiment;
        doc.setTextColor(sentiment === 'good' ? 74 : sentiment === 'bad' ? 139 : 122, 
                         sentiment === 'good' ? 124 : sentiment === 'bad' ? 74 : 117,
                         sentiment === 'good' ? 89 : sentiment === 'bad' ? 58 : 104);
        doc.text(sentiment, margin, y);
        y += 8;
      }
      
      doc.setFont('times', 'italic');
      doc.setFontSize(11);
      doc.setTextColor(45, 42, 35);
      const dreamLines = doc.splitTextToSize(`"${dreamText}"`, maxWidth);
      doc.text(dreamLines, margin, y);
      y += dreamLines.length * 6 + 10;

      // Selected omina
      const selectedRules = results.primaryRules.filter(r => selectedItems.rules.has(r.id));
      if (selectedRules.length > 0) {
        checkPageBreak(20);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(29, 26, 21);
        doc.text('Applicable Omina', margin, y);
        y += 10;
        
        selectedRules.forEach((rule) => {
          checkPageBreak(35);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.text('If:', margin, y);
          doc.setFont('helvetica', 'normal');
          const condLines = doc.splitTextToSize(rule.condition, maxWidth - 10);
          doc.text(condLines, margin + 10, y);
          y += condLines.length * 5 + 3;
          
          doc.setFont('helvetica', 'bold');
          doc.text('Then:', margin, y);
          doc.setFont('helvetica', 'normal');
          const thenLines = doc.splitTextToSize(rule.outcome, maxWidth - 10);
          doc.text(thenLines, margin + 10, y);
          y += thenLines.length * 5 + 3;
          
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(8);
          doc.setTextColor(122, 117, 104);
          const formattedCollection = formatSource(rule.collection);
          const formattedCitation = formatCitation(rule.citation);
          doc.text(`${formattedCollection} - ${formattedCitation}`, margin, y);
          y += 8;
          doc.setTextColor(45, 42, 35);
        });
        y += 5;
      }

      // Selected symbols
      const selectedSymbols = results.symbolicMeanings.filter(e => selectedItems.symbols.has(e.id));
      if (selectedSymbols.length > 0) {
        checkPageBreak(20);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(29, 26, 21);
        doc.text('Meaningful Symbols', margin, y);
        y += 10;
        
        selectedSymbols.forEach((entity) => {
          checkPageBreak(30);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.text(entity.name, margin, y);
          y += 6;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          const summaryLines = doc.splitTextToSize(entity.summary, maxWidth);
          doc.text(summaryLines, margin, y);
          y += summaryLines.length * 5 + 3;
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(8);
          doc.setTextColor(122, 117, 104);
          doc.text(formatCategory(entity.category), margin, y);
          y += 8;
          doc.setTextColor(45, 42, 35);
        });
      }

      // Selected rituals
      const selectedRituals = results.ritualRecommendations?.filter(r => selectedItems.rituals.has(r.ritual.id)) || [];
      if (selectedRituals.length > 0) {
        checkPageBreak(20);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(29, 26, 21);
        doc.text('Recommended Rituals', margin, y);
        y += 10;
        
        selectedRituals.forEach((rec) => {
          const ritual = rec.ritual;
          checkPageBreak(40);
          
          // Title
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.text(ritual.title, margin, y);
          y += 7;
          
          // Purpose
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          const purposeLines = doc.splitTextToSize(ritual.purpose, maxWidth);
          doc.text(purposeLines, margin, y);
          y += purposeLines.length * 5 + 5;
          
          // Ingredients
          if (ritual.ingredients && ritual.ingredients.length > 0) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.text('Ingredients:', margin, y);
            y += 5;
            doc.setFont('helvetica', 'normal');
            const ingredientsText = ritual.ingredients.join(', ');
            const ingredientLines = doc.splitTextToSize(ingredientsText, maxWidth - 5);
            doc.text(ingredientLines, margin + 5, y);
            y += ingredientLines.length * 4 + 5;
          }
          
          // Procedure
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(9);
          doc.text('Procedure:', margin, y);
          y += 5;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          const procedureLines = doc.splitTextToSize(ritual.procedure, maxWidth - 5);
          doc.text(procedureLines, margin + 5, y);
          y += procedureLines.length * 4 + 5;
          
          // Reference
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(8);
          doc.setTextColor(122, 117, 104);
          doc.text(ritual.reference, margin, y);
          y += 10;
          doc.setTextColor(45, 42, 35);
        });
      }

      // Save
      doc.save(`sailu-dream-analysis-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  return (
    <div
      className="sticky-dream-summary"
      style={{
        position: 'sticky',
        top: 'var(--space-4)',
        background: 'linear-gradient(135deg, rgba(245, 242, 235, 1) 0%, rgba(250, 248, 243, 1) 100%)',
        border: '1px solid var(--stone-dark)',
        padding: 'var(--space-6)',
        marginBottom: 'var(--space-8)',
        zIndex: 10,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.06)',
      }}
    >
      {/* Header row: Brand + Date + Share */}
      <div
        className="sticky-summary-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-4)',
          paddingBottom: 'var(--space-3)',
          borderBottom: '1px solid rgba(122, 117, 104, 0.2)',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <div
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: '600',
              color: 'var(--charcoal)',
            }}
          >
            <em>Šāʾilu</em> Dream Analysis
          </div>
          {sentimentAnalysis && (
            <div
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: '600',
                padding: '4px var(--space-2)',
                background:
                  sentimentAnalysis.summary.dominantSentiment === 'good'
                    ? 'rgba(74, 124, 89, 0.2)'
                    : sentimentAnalysis.summary.dominantSentiment === 'bad'
                      ? 'rgba(139, 74, 58, 0.2)'
                      : 'rgba(122, 117, 104, 0.2)',
                color:
                  sentimentAnalysis.summary.dominantSentiment === 'good'
                    ? 'var(--omen-favorable)'
                    : sentimentAnalysis.summary.dominantSentiment === 'bad'
                      ? 'var(--omen-unfavorable)'
                      : 'var(--omen-neutral)',
              }}
            >
              {sentimentAnalysis.summary.dominantSentiment}
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--dust)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {dateAnalyzed}
          </span>
          <div ref={shareMenuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              title="Export options"
              className="export-button"
              style={{
                padding: '6px 12px',
                border: '1px solid var(--stone-dark)',
                background: 'var(--papyrus-white)',
                color: 'var(--soot)',
                fontSize: 'var(--text-xs)',
                cursor: 'pointer',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
              }}
            >
              <span>↗</span> Export {showShareMenu ? '▴' : '▾'}
            </button>
            {showShareMenu && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  background: 'var(--papyrus-white)',
                  border: '1px solid var(--stone-dark)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  zIndex: 1000,
                  minWidth: '150px',
                }}
              >
                <button
                  onClick={handleShare}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--soot)',
                    fontSize: 'var(--text-xs)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontWeight: '500',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--stone-light)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  📤 Share text
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={totalSelected === 0}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: 'none',
                    background: 'transparent',
                    color: totalSelected === 0 ? 'var(--dust)' : 'var(--soot)',
                    fontSize: 'var(--text-xs)',
                    cursor: totalSelected === 0 ? 'not-allowed' : 'pointer',
                    textAlign: 'left',
                    fontWeight: '500',
                  }}
                  onMouseEnter={(e) => {
                    if (totalSelected > 0) e.currentTarget.style.background = 'var(--stone-light)';
                  }}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  title={totalSelected === 0 ? 'Select items to download PDF' : undefined}
                >
                  📄 Download PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dream text - more prominent */}
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-base)',
          lineHeight: '1.7',
          color: 'var(--charcoal)',
          fontStyle: 'italic',
        }}
      >
        &ldquo;{displayText}&rdquo;
      </p>

      {/* Expand/collapse button */}
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            marginTop: 'var(--space-3)',
            padding: '0',
            border: 'none',
            background: 'transparent',
            color: 'var(--lapis-lazuli)',
            fontSize: 'var(--text-sm)',
            cursor: 'pointer',
            fontWeight: '500',
          }}
        >
          {isExpanded ? '▲ Show less' : '▼ Show full dream'}
        </button>
      )}

      {/* Selected items summary */}
      {totalSelected > 0 && (
        <div
          style={{
            marginTop: 'var(--space-4)',
            paddingTop: 'var(--space-3)',
            borderTop: '1px solid rgba(122, 117, 104, 0.2)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--dust)',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 'var(--space-2)',
            }}
          >
            Your Interpretation ({totalSelected} selected)
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {selectedItems && selectedItems.rules.size > 0 && (
              <span
                style={{
                  fontSize: 'var(--text-xs)',
                  padding: '4px var(--space-2)',
                  background: 'rgba(41, 98, 148, 0.1)',
                  color: 'var(--lapis-lazuli)',
                  fontWeight: '500',
                }}
              >
                {selectedItems.rules.size} {selectedItems.rules.size === 1 ? 'omen' : 'omina'}
              </span>
            )}
            {selectedItems && selectedItems.symbols.size > 0 && (
              <span
                style={{
                  fontSize: 'var(--text-xs)',
                  padding: '4px var(--space-2)',
                  background: 'rgba(41, 98, 148, 0.1)',
                  color: 'var(--lapis-lazuli)',
                  fontWeight: '500',
                }}
              >
                {selectedItems.symbols.size} {selectedItems.symbols.size === 1 ? 'symbol' : 'symbols'}
              </span>
            )}
            {selectedItems && selectedItems.rituals.size > 0 && (
              <span
                style={{
                  fontSize: 'var(--text-xs)',
                  padding: '4px var(--space-2)',
                  background: 'rgba(41, 98, 148, 0.1)',
                  color: 'var(--lapis-lazuli)',
                  fontWeight: '500',
                }}
              >
                {selectedItems.rituals.size} {selectedItems.rituals.size === 1 ? 'ritual' : 'rituals'}
              </span>
            )}
          </div>
        </div>
      )}

    </div>
  );
};
